/**
 * **Promote & Publish** — "Are you sure you want to publish *X* to all JAS
 * users?" (`docs/features/group-accounts.md`, *Members choose; Promote &
 * Publish*, owner 2026-10-18).
 *
 * One group, and it is named. More than one — the platform admin, or a Group
 * Admin of several — and the box asks which, **with none ticked**, so a click
 * cannot send a desk to the wrong group's members. Each choice says what it
 * would be there: a first version, the next one, or refused because that group
 * already has a different layout of the name.
 *
 * Publishing changes nobody's layouts — members find it in Shared with me —
 * which is why one confirm is enough and there is no separate draft step.
 */

import { useState } from "react";
import type { GroupAccount } from "../api/me";
import type { PromotePlan } from "../lib/group-layouts";

export interface PromoteDialogProps {
  layoutName: string;
  /** The groups this account may publish to, each with what a publish would be there. */
  targets: ReadonlyArray<{ account: GroupAccount; plan: PromotePlan }>;
  /** Resolves to the version published; throws with the server's sentence. */
  onConfirm: (account: GroupAccount) => Promise<number | undefined>;
  onDone: (result: { account: GroupAccount; version: number | undefined }) => void;
  onCancel: () => void;
}

export function PromoteDialog({ layoutName, targets, onConfirm, onDone, onCancel }: PromoteDialogProps) {
  const only = targets.length === 1 ? targets[0]! : null;
  const [picked, setPicked] = useState<string | null>(only?.account.key ?? null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const chosen = targets.find((t) => t.account.key === picked) ?? null;
  const blocked = !chosen || chosen.plan.kind === "clash" || busy;

  const confirm = async () => {
    if (!chosen || blocked) return;
    setBusy(true);
    setError(null);
    try {
      const version = await onConfirm(chosen.account);
      onDone({ account: chosen.account, version });
    } catch (e) {
      setError(e instanceof Error && e.message ? e.message : "Publish failed");
      setBusy(false);
    }
  };

  return (
    <div style={SCRIM} onKeyDown={(e) => e.key === "Escape" && !busy && onCancel()}>
      <div role="dialog" aria-modal="true" aria-label="Promote & Publish" style={PANEL}>
        <h4 style={TITLE}>Promote &amp; Publish</h4>
        {only ? (
          <p style={BODY}>
            Are you sure you want to publish "{layoutName}" to all {only.account.name} users?
          </p>
        ) : (
          <>
            <p style={BODY}>Publish "{layoutName}" to which group?</p>
            <div role="radiogroup" aria-label="Group" style={CHOICES}>
              {targets.map(({ account }) => (
                <label key={account.key} style={CHOICE}>
                  <input
                    type="radio"
                    name="promote-group"
                    checked={picked === account.key}
                    onChange={() => setPicked(account.key)}
                  />
                  {account.name}
                </label>
              ))}
            </div>
            <p style={BODY}>
              Are you sure you want to publish it to all users of that group?
            </p>
          </>
        )}
        {chosen && <p style={NOTE}>{planText(chosen.plan, chosen.account.name, layoutName)}</p>}
        <p style={NOTE}>It appears in their Shared with me. Nobody's layouts are changed.</p>
        {error && <p style={ERROR}>{error}</p>}
        <div style={FOOT}>
          <button type="button" onClick={onCancel} disabled={busy} style={BTN}>
            Cancel
          </button>
          <button
            type="button"
            autoFocus={only !== null}
            onClick={() => void confirm()}
            disabled={blocked}
            style={{ ...BTN, ...PRIMARY, ...(blocked ? DISABLED : {}) }}
          >
            {busy
              ? "Publishing…"
              : chosen?.plan.kind === "next"
                ? `Yes, publish v${chosen.plan.from + 1}`
                : "Yes, publish"}
          </button>
        </div>
      </div>
    </div>
  );
}

function planText(plan: PromotePlan, group: string, name: string): string {
  switch (plan.kind) {
    case "new":
      return "New · v1";
    case "next":
      return `Replaces v${plan.from} → v${plan.from + 1}. Members who have it see ● new. The copy in Author as ${group} is not updated — publishing that one again would put it back.`;
    case "clash":
      return `${group} already has a layout called "${name}" — rename yours first.`;
  }
}

const SCRIM: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  zIndex: 1100,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "rgba(0,0,0,.35)",
};

const PANEL: React.CSSProperties = {
  width: 380,
  maxWidth: "calc(100vw - 32px)",
  padding: "14px 16px 16px",
  background: "var(--qf-surface)",
  border: "1px solid var(--qf-border)",
  borderRadius: 5,
  boxShadow: "0 14px 40px -12px rgba(0,0,0,.6)",
  color: "var(--qf-text)",
  fontSize: 12,
};

const TITLE: React.CSSProperties = { margin: "0 0 13px", fontSize: 13, fontWeight: 600 };

const BODY: React.CSSProperties = { margin: "0 0 8px" };

const NOTE: React.CSSProperties = { margin: "0 0 6px", color: "var(--qf-text-muted)" };

const ERROR: React.CSSProperties = { margin: "6px 0 0", color: "var(--qf-negative)" };

const CHOICES: React.CSSProperties = { display: "flex", flexDirection: "column", gap: 4, margin: "0 0 10px" };

const CHOICE: React.CSSProperties = { display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer" };

const FOOT: React.CSSProperties = {
  display: "flex",
  justifyContent: "flex-end",
  flexWrap: "wrap",
  gap: 7,
  marginTop: 16,
};

const BTN: React.CSSProperties = {
  border: "1px solid var(--qf-border)",
  background: "var(--qf-background)",
  color: "var(--qf-text)",
  borderRadius: 3,
  padding: "4px 10px",
  font: "inherit",
  fontSize: 11.5,
  cursor: "pointer",
};

const PRIMARY: React.CSSProperties = {
  background: "var(--qf-accent)",
  borderColor: "var(--qf-accent)",
  color: "var(--qf-background)",
  fontWeight: 600,
};

const DISABLED: React.CSSProperties = { opacity: 0.5, cursor: "default" };
